import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeftIcon, UserCircleCheckIcon } from "@phosphor-icons/react";

import {
  EditCreatorPageWrapperStyles,
  FormFooterStyled,
  PageScrollWrapperStyles
} from "./styled";

import Button from "components/Buttons/index";
import LoadingSpinner from "components/PageLoader/Spinner";

import { useEditCreatorForm } from "./hooks/useEditCreatorForm";
import { useDropdownMenuItems } from "./hooks/useDropdownMenuItems";
import { CreatorInfoSection } from "./components/CreatorInfoSection";
import { CreatorPlatformsSection } from "./components/CreatorPlatformsSection";
import { CreatorCommercialsSection } from "./components/CreatorCommercialsSection";

const CreatorsPage = () => {
  const {
    // Data
    form,
    setForm,
    data,
    hasChanges,

    // Loading
    loading,
    updateLoading,
    citiesLoading,
    languageLoading,
    primaryCategoriesLoading,
    subCategoriesLoading,
    talentAgencyManagersLoading,

    // Dropdown raw data
    States,
    Cities,
    Companies,
    Languages,
    PrimaryCategories,
    SubCategories,
    TalentAgencyManagers,

    // Handlers
    handleChange,
    handleSave,
    handleStateChange,
    handleCityChange,
    handleTalentCompanyChange,
    handlePrimaryCategoryChange,
    handleSubCategoryChange,
    handlePrimaryCategorySearch,
    handleSubCategorySearch,
    handleLanguageSearch,

    // Navigation
    navigate,
    location,

    fieldErrors,

    phoneError,
    setPhoneError,
    emailError,
    setEmailError,
    managerPhoneError,
    setManagerPhoneError,
    managerEmailError,
    setManagerEmailError,
    hasErrors
  } = useEditCreatorForm();

  const dropdownItems = useDropdownMenuItems({
    States,
    Cities,
    Companies,
    Languages,
    PrimaryCategories,
    SubCategories,
    TalentAgencyManagers,
    form
  });

  const isSaveDisabled = () => {
    if (!hasChanges || updateLoading || hasErrors) return true;

    if (form.managed_by === "self_managed") {
      return !form.phone || form.phone.length < 10;
    }

    if (form.managed_by === "personal_managed") {
      return !form.personal_manager_name || !form.personal_manager_phone;
    }

    if (form.managed_by === "talent_managed") {
      return !form.talent_company_id || !form.talent_manager_id;
    }

    return false;
  };

  return (
    <EditCreatorPageWrapperStyles>
      {/* ── Header ── */}
      <div className="header-wrapper">
        <div
          className="go-back-btn"
          onClick={() => navigate(`/creators${location.search}`)}
        >
          <ArrowLeftIcon weight="bold" size={20} color="#1F1F1F" />
        </div>

        <h1 className="title">Edit Creator: {form?.name}</h1>

        {form?.is_creator_user && (
          <div className="verified-badge">
            <UserCircleCheckIcon weight="fill" size={12} color="#8C63CC" />
            <span className="badge-text">Creator User</span>
          </div>
        )}
      </div>

      {/* ── Body ── */}
      {loading ? (
        <PageScrollWrapperStyles>
          <LoadingSpinner
            color="#797979"
            text="Loading..."
            fontSize={14}
            size={16}
            className="loading-spinner"
          />
        </PageScrollWrapperStyles>
      ) : (
        <PageScrollWrapperStyles>
          <CreatorInfoSection
            form={form}
            setForm={setForm}
            handleChange={handleChange}
            handleStateChange={handleStateChange}
            handleCityChange={handleCityChange}
            handleTalentCompanyChange={handleTalentCompanyChange}
            handlePrimaryCategoryChange={handlePrimaryCategoryChange}
            handleSubCategoryChange={handleSubCategoryChange}
            handlePrimaryCategorySearch={handlePrimaryCategorySearch}
            handleSubCategorySearch={handleSubCategorySearch}
            handleLanguageSearch={handleLanguageSearch}
            citiesLoading={citiesLoading}
            languageLoading={languageLoading}
            primaryCategoriesLoading={primaryCategoriesLoading}
            subCategoriesLoading={subCategoriesLoading}
            talentAgencyManagersLoading={talentAgencyManagersLoading}
            fieldErrors={fieldErrors}
            phoneError={phoneError}
            setPhoneError={setPhoneError}
            emailError={emailError}
            setEmailError={setEmailError}
            managerPhoneError={managerPhoneError}
            setManagerPhoneError={setManagerPhoneError} // ← this must come from the hook
            managerEmailError={managerEmailError}
            setManagerEmailError={setManagerEmailError}
            {...dropdownItems}
          />

          <CreatorPlatformsSection
            form={form}
            setForm={setForm}
            handleChange={handleChange}
          />

          <CreatorCommercialsSection
            form={form}
            setForm={setForm}
            handleChange={handleChange}
            data={data}
          />
        </PageScrollWrapperStyles>
      )}

      {/* ── Footer ── */}
      <FormFooterStyled>
        <Button
          variant="secondary"
          onClick={() => navigate(`/creators${location.search}`)}
          disabled={updateLoading}
        >
          Cancel
        </Button>

        <Button
          size="M"
          variant="primary"
          onClick={handleSave}
          disabled={isSaveDisabled()}
        >
          {updateLoading ? "Saving..." : "Save Changes"}
        </Button>
      </FormFooterStyled>
    </EditCreatorPageWrapperStyles>
  );
};

export default CreatorsPage;
